import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

import {
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  PersonOutline as PersonIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  LocalShipping as VehicleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  DirectionsCar as JourneyIcon,
  CalendarMonth as CalendarIcon,
  ChatBubbleOutline as MessageIcon,
  Phone as PhoneIcon,
  WarningAmber as WarningIcon,
  Home as HomeIcon,
  DescriptionOutlined as DocumentsIcon,
  MoreHoriz as MoreIcon,
  ChevronRight as ChevronRightIcon,
  Festival,
  TransferWithinAStation,
  Business,
} from '@mui/icons-material'
import API from '../../../../services/API'
import AmbulanceIcon from '../../../../assets/icons/Ambulance'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import { useNavigate } from 'react-router-dom'
import ErrorModal from '../../../../components/Modals/Error'
import LateStart from './LateStart'
import LateFinish from './LateFinish'

const TERMINAL_PTS_JOURNEY_STATUSES = [
  'Completed',
  'Cancelled',
  'No Show',
  'Aborted',
]

const DetailItem = ({ icon, label, children }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    <Box sx={{ mt: 0.2 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  </Stack>
)

const StatusDot = ({ shift }) => {
  const status = shift?.clock?.endedAt
    ? 'Shift Complete'
    : shift?.clock?.startedAt
      ? 'On Shift'
      : 'Not Started'

  const color = shift?.clock?.endedAt
    ? 'contrast.main'
    : shift?.clock?.startedAt
      ? 'success.main'
      : 'error.main'

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }}
      />
      <Typography variant="body2" fontWeight={800}>
        {status}
      </Typography>
    </Stack>
  )
}

const ActiveShift = () => {
  const [shift, setShift] = useState({})
  const [error, setError] = useState(null)
  const [needsLateStartReason, setNeedsLateStartReason] = useState(false)
  const [needsLateFinishReason, setNeedsLateFinishReason] = useState(false)
  const [now, setNow] = useState(moment())
  const { baseLocationOptions, tenantInfo } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const navigate = useNavigate()
  const shiftClock = {
    earlyStartWindowMinutes: 15,
    onTimeGraceMinutes: 15,
    lateStartAfterMinutes: 15,
    earlyFinishBeforeMinutes: 15,
    lateFinishAfterMinutes: 15,
    ...(orgSettings?.shiftClock ?? {}),
  }

  const loadActiveShift = () => {
    API.get('/active-shift').then((res) => {
      setShift(res.data)
    })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(moment())
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    loadActiveShift()

    const interval = setInterval(() => {
      loadActiveShift()
    }, 120000)
    return () => clearInterval(interval)
  }, [])

  const hasStarted = !!shift?.clock?.startedAt
  const hasEnded = !!shift?.clock?.endedAt
  const isPastShift = moment(shift.end).isBefore(now)
  const shiftHeading = isPastShift
    ? hasStarted
      ? hasEnded
        ? 'Previous Shift'
        : 'Active Shift'
      : 'Previous Shift - Not Clocked In'
    : moment(shift.start).isAfter(now) && !hasStarted
      ? 'Upcoming Shift'
      : 'Active Shift'

  const ptsJourneys = (shift?.ptsDuty?.routes ?? []).flatMap(
    (route) => route?.journeys ?? [],
  )
  const openPTSJourneyCount =
    shift?.shiftType === 'PTS'
      ? ptsJourneys.filter(
          (journey) =>
            !TERMINAL_PTS_JOURNEY_STATUSES.includes(journey?.status),
        ).length
      : 0
  const hasOpenPTSJourneys = openPTSJourneyCount > 0

  const tooEarlyToClockIn =
    !hasStarted &&
    moment(shift.start)
      .subtract(shiftClock.earlyStartWindowMinutes, 'minutes')
      .isAfter(now)

  const tooEarlyToClockOut =
    hasStarted &&
    !hasEnded &&
    moment(shift.end)
      .subtract(shiftClock.earlyFinishBeforeMinutes, 'minutes')
      .isAfter(now)

  const ptsClockOutBlocked = hasStarted && !hasEnded && hasOpenPTSJourneys
  const showPTSDutyAction =
    shift?.shiftType === 'PTS' &&
    hasStarted &&
    !hasEnded &&
    (tooEarlyToClockOut || ptsClockOutBlocked)

  const clockButtonDisabled =
    tooEarlyToClockIn || tooEarlyToClockOut || ptsClockOutBlocked

  const clockInAvailableFrom = moment(shift.start).subtract(
    shiftClock?.earlyStartWindowMinutes ?? 30,
    'minutes',
  )

  const handleClock = () => {
    if (!shift?.clock?.startedAt) {
      // Clock In
      if (
        moment().isAfter(
          moment(shift.start).add(shiftClock.lateStartAfterMinutes, 'minutes'),
        )
      ) {
        setNeedsLateStartReason(true)
      } else {
        API.post('/shift-clock-in', {
          shiftID: shift._id,
        }).then((res) => {
          if (res?.data?.errorCode) {
            setError(res.data)
          } else {
            loadActiveShift()
          }
        })
      }
    } else {
      // Clock Out
      if (
        moment().isAfter(
          moment(shift.end).add(shiftClock.lateFinishAfterMinutes, 'minutes'),
        )
      ) {
        setNeedsLateFinishReason(true)
      } else {
        API.post('/shift-clock-out', {
          shiftID: shift._id,
        }).then((res) => {
          if (res?.data?.errorCode) {
            setError(res.data)
          } else {
            loadActiveShift()
          }
        })
      }
    }
  }

  const shiftTypeColor =
    shift.shiftType === 'Event'
      ? '#E32525'
      : shift.shiftType === 'PTS'
        ? '#85276A'
        : shift.shiftType === 'FrontLine'
          ? '#239AA4'
          : '#1F3D7C'

  const baseLocationText =
    shift?.event?.baseLocation?.name ??
    shift?.ptsDuty?.baseLocation?.name ??
    shift?.roster?.baseLocation?.name ??
    shift?.frontlineDuty?.baseLocation?.name ??
    baseLocationOptions?.[0]?.name ??
    ''

  const baseLocationAddress =
    (shift?.event?.baseLocation?.address ??
    shift?.ptsDuty?.baseLocation?.address ??
    shift?.roster?.baseLocation?.address ??
    shift?.frontlineDuty?.baseLocation?.address ??
    orgSettings?.address)
      ? {
          ...orgSettings?.address,
          lineOne: orgSettings?.address?.line1,
          lineTwo: orgSettings?.address?.line2,
        }
      : {}

  const baseLocationAddressString = [
    baseLocationAddress?.lineOne,
    baseLocationAddress?.lineTwo,
    baseLocationAddress?.town,
    baseLocationAddress?.county,
    baseLocationAddress?.postCode,
  ]
    .filter(Boolean)
    .join(', ')

  if (!shift || !Object.keys(shift).length) return null

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            p: 1.5,
            borderLeft: '6px solid',
            borderColor: shiftTypeColor,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: `${shiftTypeColor}14`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: shiftTypeColor,
                flexShrink: 0,
              }}
            >
              {shift.shiftType === 'Event' ? (
                <Festival />
              ) : shift.shiftType === 'PTS' ? (
                <TransferWithinAStation />
              ) : shift.shiftType === 'FrontLine' ? (
                <AmbulanceIcon />
              ) : (
                <Business />
              )}
            </Box>

            <Box minWidth={0}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography fontWeight={900} fontSize="1rem">
                  {shiftHeading}
                </Typography>

                <Chip
                  label={shift.shiftType}
                  size="small"
                  sx={{
                    height: 22,
                    borderRadius: 1,
                    bgcolor: `${shiftTypeColor}14`,
                    color: shiftTypeColor,
                    fontWeight: 800,
                  }}
                />

                <Chip
                  label={
                    shift?.clock?.endedAt
                      ? 'Finished'
                      : shift?.clock?.startedAt
                        ? 'On Shift'
                        : 'Not Started'
                  }
                  size="small"
                  sx={{
                    height: 22,
                    borderRadius: 1,
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mt: 0.4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {moment(shift.start).format(
                    moment(shift.start).isBefore(moment().startOf('day'))
                      ? 'Do MMM HH:mm'
                      : 'HH:mm',
                  )}{' '}
                  –{' '}
                  {moment(shift.end).format(
                    moment(shift.end).isBefore(moment().startOf('day'))
                      ? 'Do MMM HH:mm'
                      : 'HH:mm',
                  )}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {baseLocationText || 'No base'}
                </Typography>

                {shift.clinicalRole && (
                  <Typography variant="body2" color="text.secondary">
                    {shift.clinicalRole}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Typography variant="body2" color="text.secondary">
              {tooEarlyToClockIn
                ? `Clock in from ${clockInAvailableFrom.format('HH:mm')}`
                : tooEarlyToClockOut
                  ? `Clock out from ${moment(shift.end)
                      .subtract(shiftClock.earlyFinishBeforeMinutes, 'minutes')
                      .format('HH:mm')}`
                  : ptsClockOutBlocked
                    ? `${openPTSJourneyCount} PTS journey${openPTSJourneyCount === 1 ? '' : 's'} open`
                  : hasStarted
                    ? `Started ${moment(shift.start).fromNow()}`
                    : 'Ready to clock in'}
            </Typography>

            {showPTSDutyAction && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/pts')}
                sx={{
                  borderRadius: 999,
                  px: 2,
                  fontWeight: 800,
                  borderColor: shiftTypeColor,
                  color: shiftTypeColor,
                }}
              >
                Open PTS Duty
              </Button>
            )}

            {!shift?.clock?.startedAt || !shift?.clock?.endedAt ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleClock()}
                sx={{
                  borderRadius: 999,
                  px: 2.5,
                  fontWeight: 800,
                  bgcolor: shiftTypeColor,
                  '&:hover': {
                    bgcolor: shiftTypeColor,
                    filter: 'brightness(0.9)',
                  },
                }}
                disabled={clockButtonDisabled}
              >
                {!shift?.clock?.startedAt ? 'Start Shift' : 'End Shift'}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
      <ErrorModal
        open={!!error}
        onClose={() => {
          setError(null)
        }}
        title={error?.error}
      />
      <LateStart
        shiftID={shift._id}
        expectedTime={shift.start}
        needsLateStartReason={needsLateStartReason}
        setNeedsLateStartReason={setNeedsLateStartReason}
        loadActiveShift={loadActiveShift}
      />
      <LateFinish
        shiftID={shift._id}
        expectedTime={shift.end}
        needsLateFinishReason={needsLateFinishReason}
        setNeedsLateFinishReason={setNeedsLateFinishReason}
        loadActiveShift={loadActiveShift}
      />
    </Grid>
  )
}

export default ActiveShift
